import { useState, useEffect, useRef, useCallback } from "react";
import AdTables from "../../components/AdFlow/AdTables";
import AdPlaceBidSection from "../../components/AdFlow/AdPlaceBidSection";
import VBLeaflet from '@vb-js-commons/new/vbLeaflet';
import Cookies from 'js-cookie';
import VBLoadingScreen from '@vb-js-commons/vbLoadingScreen';
import { getProp, searchProps } from "../../api/services/property";
import { sendAdForReview, bookmarkAd } from "../../api/services/ad";
import { ErrorModal } from "../../modals";

import Navigation from '../../components/static/Navigation';
import Footer from '../../components/static/Footer';
import { useAuctionProtectedSitesWms } from './auctionProtectedSitesWms';
import AuctionDetailsMapControls from './AuctionDetailsMapControls';
import {AuctionWmsIdentifyModal } from './AuctionWmsIdentifyModal';
import { adGeometryOmittingPoints } from '../../components/minskog/comps/SellTimber/helpers/api';
import { addPlacedMarkersToFeatureGroup } from '../../components/minskog/comps/SellTimber/helpers/sellTimberMapMarkers';
import { isGrotArrangement } from '../../components/minskog/comps/SellTimber/helpers/sellTimberShared';

/* global L */

/** `#id muni` plus kontraktstyp (e.g. Leveransvirke, Biobränsle) when not already in `ad.label`. */
function formatAuctionAdHeadline(ad) {
    const base = ad?.label?.trim() || '';
    if (!ad) return base;
    const suffix =
        (ad.name_display && String(ad.name_display).trim()) ||
        (ad.type && String(ad.type).trim()) ||
        '';
    if (!suffix || base.toLowerCase().includes(suffix.toLowerCase())) return base;
    return `${base} - ${suffix}`;
}

const AuctionDetailsPage = () => {
    const [ad, setAd] = useState(Window.react_data.ad);
    const [bookmarked, setBookmarked] = useState(Window.react_data.ad ? Window.react_data.ad.bookmarked : false);
    const [loading, setLoading] = useState(false);
    const [directionCoords, setDirectionCoords] = useState(
        Window.react_data.ad.longitude && Window.react_data.ad.latitude ? `${Window.react_data.ad.latitude},${Window.react_data.ad.longitude}` : "0,0"
    );
    const [errorModal, setErrorModal] = useState(false);
    const [errorModalText, setErrorModalText] = useState('');
    const [geoJsonLayers, setGeoJsonLayers] = useState(null);
    const [mapFullscreen, setMapFullscreen] = useState(false);
    const [wmsIdentifyModal, setWmsIdentifyModal] = useState({ open: false, loading: false, sections: [] });

    const mapRef = useRef(null);
    const mapWrapRef = useRef(null);
    const [mapModalContainer, setMapModalContainer] = useState(null);
    const geojsonLayersRef = useRef(null);
    const attachmentRef = useRef();

    const onWmsIdentifyStart = useCallback(() => {
        setWmsIdentifyModal({ open: true, loading: true, sections: [] });
    }, []);

    const onWmsIdentifyComplete = useCallback((sections) => {
        setWmsIdentifyModal((prev) => ({ ...prev, loading: false, sections: sections || [] }));
    }, []);

    const closeWmsIdentifyModal = useCallback(() => {
        setWmsIdentifyModal({ open: false, loading: false, sections: [] });
    }, []);

    useAuctionProtectedSitesWms(mapRef, {
        onIdentifyStart: onWmsIdentifyStart,
        onIdentifyComplete: onWmsIdentifyComplete,
    });

      // Map related
    const fitMapToPlacedMarkers = (map, placedList) => {
        if (!Array.isArray(placedList) || !placedList.length) return;
        const first = placedList[0];
        const lat = Number(first?.lat);
        const lng = Number(first?.lng);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
            map.setView([lat, lng], 14);
        }
    };

    const updateMapWithSelectedProperty = async (registerenhet) => {
        const borders = L.featureGroup([]);
        const response = await getProp(registerenhet);
        if (response.ok) {
            const propertyLayer = L.geoJson(response.data.geojson, { color: "#ffffff", fillColor: "transparent" });
            propertyLayer.bringToBack();
            borders.addLayer(propertyLayer);
            const auctionLayers = geojsonLayersRef.current;
            if (auctionLayers) {
                auctionLayers.eachLayer((layer) => borders.addLayer(layer));
            }
            mapRef.current.setLayers(borders);
            borders.addTo(mapRef.current.state.map);
            const map = mapRef.current.state.map;
            const isGrot = isGrotArrangement(ad);
            try {
                const propertyBounds = propertyLayer.getBounds();
                if (isGrot && propertyBounds?.isValid?.()) {
                    map.fitBounds(propertyBounds, { padding: [24, 24] });
                } else if (ad.geom && Object.keys(ad.geom).length !== 0 && geojsonLayersRef.current) {
                    const layerBounds = geojsonLayersRef.current.getBounds();
                    if (layerBounds?.isValid?.()) {
                        map.fitBounds(layerBounds);
                    } else if (propertyBounds?.isValid?.()) {
                        map.fitBounds(propertyBounds);
                    }
                } else if (propertyBounds?.isValid?.()) {
                    map.fitBounds(propertyBounds);
                }
            } catch {
                if (isGrot) {
                    fitMapToPlacedMarkers(map, ad.placed_map_markers);
                }
            }
            setLoading(false);
        } else {
            handleServerError(response.status, 'Försök igen senare eller kontakta oss.', 'Den här funktionen är inte tillgänglig.');
            const map = mapRef.current?.state?.map;
            if (map && isGrotArrangement(ad)) {
                const layers = geojsonLayersRef.current;
                try {
                    const bounds = layers?.getBounds?.();
                    if (bounds?.isValid?.()) {
                        map.fitBounds(bounds);
                    } else {
                        fitMapToPlacedMarkers(map, ad.placed_map_markers);
                    }
                } catch {
                    fitMapToPlacedMarkers(map, ad.placed_map_markers);
                }
            }
        }
    };

    const searchProp = async (val) => {
        const result = await searchProps(val);
        if (result.ok) updateMapWithSelectedProperty(result.data[0].registerenhet);
    };

    useEffect(() => {
        const drawnItems = L.featureGroup([]);
        setTimeout(() => {
            if (mapRef.current && Window.react_data.trustedBuyer) {
                const { ad } = Window.react_data;
                if (ad?.description_of_property) {
                    ad.description_of_property.split('&').forEach(desc => searchProp(desc.trim()));
                }
                const placedList = ad.placed_map_markers;
                const hasPlaced = Array.isArray(placedList) && placedList.length > 0;
                const hasGeom = ad?.geom && Object.keys(ad.geom).length !== 0;
                const isGrot = isGrotArrangement(ad);
                const willLoadPropertyBorder = Boolean(ad?.description_of_property?.trim());
                if (hasGeom || hasPlaced) {
                    const geomForPoly = hasPlaced && hasGeom
                        ? adGeometryOmittingPoints(ad.geom)
                        : hasGeom
                          ? ad.geom
                          : null;
                    if (geomForPoly && Object.keys(geomForPoly).length > 0) {
                        const polyLayer = L.geoJson(geomForPoly, { color: "#DFFF5D" });
                        drawnItems.addLayer(polyLayer);
                    }
                    if (hasPlaced) {
                        addPlacedMarkersToFeatureGroup(placedList, drawnItems);
                    } else {
                        const geoJson = L.geoJson(ad.geom, { color: "#DFFF5D" });
                        drawnItems.addLayer(geoJson);
                    }
                    mapRef.current.setLayers(drawnItems);
                    drawnItems.addTo(mapRef.current.state.map);
                    const map = mapRef.current.state.map;
                    const deferZoomToPropertyBorder = isGrot && willLoadPropertyBorder;
                    if (drawnItems.getLayers().length && !deferZoomToPropertyBorder) {
                        try {
                            const bounds = drawnItems.getBounds();
                            if (bounds.isValid()) {
                                map.fitBounds(bounds);
                            } else if (hasPlaced) {
                                fitMapToPlacedMarkers(map, placedList);
                            }
                        } catch {
                            if (hasPlaced) {
                                fitMapToPlacedMarkers(map, placedList);
                            }
                        }
                    }
                    setGeoJsonLayers(drawnItems);
                }
            }
        }, 500);
    }, []);

    useEffect(() => {
        const syncMapFullscreen = () => {
            const el = mapWrapRef.current;
            const active = !!(
                el &&
                (document.fullscreenElement === el || document.webkitFullscreenElement === el)
            );
            setMapFullscreen(active);
            const m = mapRef.current?.state?.map;
            if (m) {
                setTimeout(() => m.invalidateSize(), 250);
            }
        };
        document.addEventListener('fullscreenchange', syncMapFullscreen);
        document.addEventListener('webkitfullscreenchange', syncMapFullscreen);
        return () => {
            document.removeEventListener('fullscreenchange', syncMapFullscreen);
            document.removeEventListener('webkitfullscreenchange', syncMapFullscreen);
        };
    }, []);

    useEffect(()=>{
        geojsonLayersRef.current = geoJsonLayers
    },[geoJsonLayers])

    //API
    const handleServerError = (status, text500, text400) => {
        if (status >= 500) {
            setErrorModalText(text500);
            setErrorModal(true);
        } else if (status >= 400) {
            setErrorModalText(text400);
            setErrorModal(true);
        }
    };

    const sendForReview = async () => {
        setLoading(true);
        Cookies.remove('ad_id');
        const response = await sendAdForReview(window.location);
        if (response.ok) {
            window.location.href = window.location.href.replace("/review/", "")
        } else {
            handleServerError(resp.status, 'Försök igen senare eller kontakta oss.', 'Den här funktionen är inte tillgänglig.');
        }
    }

  

    const handleBookmarkClick = async (event) => {
        event.stopPropagation();
        const response = await bookmarkAd(window.location);
        if (response.ok) {
            setBookmarked(response.data.bookmarked);
            setLoading(false)
        }
        else {
            console.error("Error setting bookmark", response.status)
        }
    }

    // helpers
    const redirectAfterServerError = () => {
        window.location.href = '/'
    }
    const cleanNumbers = (num) => {
        return Math.round(Number(num)).toString().replace(".0", "").replace(".", ",")
    }

    const reviewActions = () => {
        return(<div className='flex justify-center lg:justify-start items-center mt-2'>
            <a className='border border-solid n-btn btn-sm mr-4 bg-gray-100 hover:bg-white' href="/auctions/create/">Redigera</a>
            {/* Submit for review */}
            <button className="n-btn btn-core btn-sm flex items-center cursor-pointer" onClick={() => sendForReview()}>Skicka till granskning <img className={`ml-2 neutral-charcoal-filter`} style={{ width: "16px", height: "16px" }} src={Window.static_path + "img/icons/arrow-black.svg"} /></button>
        </div>)
    }

    return <div className="w-full flex flex-col h-screen justify-between p-0">
        <VBLoadingScreen show_loading={loading} disable_reload_timeout={false} loading_text={null} />

        <Navigation />

        <section className='bg-white fs-6 pt-0 md:pt-3'>
            {ad.should_display_popup ? <>
                {Window.user_data.isBanned ? <>
                    <div className="container">
                        <div className="flex flex-wrap md:flex-row items-start justify-center">
                            <div className='md:w-1/2 w-full'>
                                <img src={`${Window.static_path}img/icons/Warning.svg`} className='tertiary-red-filter mr-1' height='16px' width='16px' alt='Warning Icon' />
                                <p className='text-center mb-0 tertiary-red subtitle font-bold mb-6'>
                                    Du har inte tillgång till att öppna och se annonser, kontakta Treebula för mer information på
                                    <a className='underline font-bold' style={{ cursor: 'pointer' }} href="tel:00468339944"> 08339944</a> eller maila till <a className='underline font-bold' style={{ cursor: 'pointer' }} href="mailto:info@virkesborsen.se">info@virkesborsen.se.</a>
                                </p>
                            </div>
                        </div>
                    </div>
                </> : <>
                    <div className="container">
                        <div className="flex flex-wrap md:flex-row">

                            {/*Ads */}
                            <div className='p-2 mb-6 order-1 md:order-1 w-full'>

                                <div className='flex items-center justify-center mb-6'>
                                    <p className="ml-2 fs-3 md-!fs-2 font-medium text-center flex"><span className="flex justify-center pr-1"><img onClick={(e) => { handleBookmarkClick(e) }}
                                        src={`${Window.static_path}img/icons/${bookmarked ? 'bookmark_star_orange.svg' : 'bookmark_star.svg'}`}
                                        height='24px' width='24px' alt='BookMark' /></span> {formatAuctionAdHeadline(ad)}
                                        {ad && ad.fullserviceadorder && !actionIsLive && ad.fullserviceadorder.state_display_name}</p>
                                </div>

                                <div className='flex flex-wrap column items-center justify-center'>
                                    {isGrotArrangement(ad) ? (
                                        ad.grot_volume ? (
                                            <>
                                                <p className='mr-2 mb-0 fs-6 font-medium'>
                                                    {String(ad.grot_volume).trim()} m³
                                                </p>
                                                <span className='mr-2'>|</span>
                                            </>
                                        ) : null
                                    ) : ad.total_volume ? (
                                        <>
                                            <p className='mr-2 mb-0 fs-6 font-medium'>
                                                {cleanNumbers(ad.total_volume)} m³fub
                                            </p>
                                            <span className='mr-2'>|</span>
                                        </>
                                    ) : null}
                                    <p className='mr-2 mb-0 fs-6'>{ad.arrangement_types} </p>
                                    {ad.auction_is_over && (
                                        <p className='mb-0 fs-6'><span className='mr-2'>|</span>Slutdatum: {ad.end_time}</p>
                                    )}
                                    {ad.auction_in_future && (
                                        <p className='mb-0 fs-6'><span className='mr-2'>|</span>Startdatum: {ad.start_time}</p>
                                    )}
                                    {ad.auction_is_live && (
                                        <p className='mb-0 fs-6'><span className='mr-2'>|</span>Slutdatum: {ad.end_time}</p>
                                    )}
                                </div>
                            </div>

                            {/*Left Section*/}
                            <div className="bg-white primary-dark w-full lg:w-2/3 md:w-1/2 mb-6 order-3 md:order-2 pr-2">
                                {/*Leaflet Map*/}
                                <div
                                    ref={(el) => {
                                        mapWrapRef.current = el;
                                        setMapModalContainer(el);
                                    }}
                                    className={`flex justify-center relative ${wmsIdentifyModal.open ? 'overflow-visible' : 'overflow-hidden'}`}
                                    style={{
                                        height: mapFullscreen ? '100vh' : '500px',
                                        borderRadius: mapFullscreen ? 0 : '16px',
                                        width: mapFullscreen ? '100%' : undefined,
                                    }}
                                >
                                    {Window.react_data.trustedBuyer ? null : (
                                        <div
                                            className='flex items-center justify-center px-6 absolute'
                                            style={{
                                                backgroundColor: 'rgba(0,0,0,0.7)',
                                                width: '100%',
                                                height: mapFullscreen ? '100%' : '500px',
                                                zIndex: 1010,
                                                borderRadius: mapFullscreen ? 0 : '15px',
                                            }}
                                        >
                                            <a href="/kontakta-oss/" className='n-btn btn-core'>Kontakta oss för att se all information</a>
                                        </div>
                                    )}
                                    <VBLeaflet ref={mapRef} id='map_auctions' baseMap='Mapbox Satelite' />
                                    <AuctionDetailsMapControls
                                        mapRef={mapRef}
                                        fullscreenTargetRef={mapWrapRef}
                                        isFullscreen={mapFullscreen}
                                    />
                                </div>

                                {Window.react_data.trustedBuyer ? <div className="flex items-center justify-center p-6" ><button className="n-btn btn-forest-green cursor-pointer" onClick={() => { window.open(`https://www.google.com/maps/dir//${directionCoords}/`, "_blank") }}>Vägbeskrivning</button></div> : null}

                                {/*All Tables*/}
                                <AdTables attachmentRef={attachmentRef} />
                            </div>

                            {/*Right Section*/}
                            <div className="bg-white primary-dark w-full lg:w-1/3 md:w-1/2 order-2 md:order-3 pl-2">
                                {/* only for Adreview */}
                                {/* only tablet & DT view */}
                                {window.location.href.includes("/review") ?
                                    <div className='hidden mb-6 shadow-xl p-6 md:flex flex-col justify-center bg-neutral-cloud sticky top-6 rounded-xl'>
                                        {/* Edit Ad */}
                                        <div className='container'>
                                            <p className='ml-2 fs-5 font-medium mb-0'>Välj en för att fortsätta med din annons:</p>
                                            {reviewActions()}
                                        </div>
                                    </div> :
                                    <AdPlaceBidSection toggle_loading={() => { setLoading(!loading); }} scrollCallback={()=>{attachmentRef.current.scrollIntoView({ behavior: 'smooth' })}} />}
                            </div>
                        </div>
                    </div>                 
                    {/* only for Adreview */}
                    {/* only for Mobile view*/}
                    {window.location.href.includes("/review") ?
                        <div className="md:hidden sticky bg-stone-100 w-full py-6 start-0 bottom-0" style={{ zIndex: 1000 }}>
                            {/* Edit Ad */}
                            <div className='container'>
                                {reviewActions()}
                            </div>
                        </div> : null}
                    <ErrorModal show={errorModal} title={"Oops! Något gick fel."} message={errorModalText} onToggle={() => {setErrorModal(false); redirectAfterServerError()}} />
                    <AuctionWmsIdentifyModal
                        show={wmsIdentifyModal.open}
                        loading={wmsIdentifyModal.loading}
                        sections={wmsIdentifyModal.sections}
                        onHide={closeWmsIdentifyModal}
                        container={mapModalContainer || undefined}
                    />
                </>}
            </> : <>
                <div className="container neutral-charcoal">
                    <div className="flex flex-wrap md:flex-row justify-center">
                        <div className='md:w-1/2 w-full'>
                            {/* Conditions for opening an ad */}
                            <h1 className="mb-2 fs-4 text-center mt-6">Villkor för öppning av annons</h1>
                            {/* Click "I agree" to see the full information and confirm that you have read our terms of use and thus may not submit tenders directly to the forest owner on this advertisement. Read our terms of use */}
                            <p className='text-center fs-6 my-4'>Klicka "Jag godkänner" för att se den fullständiga informationen och bekräfta att du har tagit del av våra användarvillkor. Läs våra användarvillkor
                                <a className='supp-spruce underline font-bold' style={{ cursor: 'pointer' }} href="/terms" target="_blank" rel="noopener noreferrer"> här.</a>
                            </p>
                            <div className="flex flex-wrap md:flex-row justify-center mt-4">
                                <form className='md:w-2/3 w-full' method="post">
                                    <input type="hidden" name="csrfmiddlewaretoken" value={Cookies.get('csrftoken')} />
                                    <input
                                        type="submit"
                                        data-track="true"
                                        data-eventName="Opened Ad"
                                        data-eventData={`{"ad_end_date":"_date${ad.end_time}"}`}
                                        className='n-btn btn-core whitespace-nowrap w-full mb-3 btn-sm block' style={{ minWidth: '100px' }}
                                        value="Jag godkänner"
                                        name="accept_terms"
                                    />
                                    {/* Cancel */}
                                    <a className='n-btn btn-outline btn-charcoal btn-sm whitespace-nowrap w-full mb-6 block' style={{ minWidth: '100px' }} href="/">
                                        Avbryt
                                    </a>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </>}
        </section>

        <Footer />
    </div>
}

export default AuctionDetailsPage;